// Retry intents: per-copy changes the reconcile could not confirm, kept until
// the user dismisses them or their Retry settles (peek/ack, never
// take-on-read: an unmount between showing and acting must not lose one).
//
//  - Keyed by (user, collection). The per-copy screen of THAT pair shows them,
//    so one that gave up while the screen was away is offered again later.
//  - Deduplicated per (pair, copy line, operation kind), keeping the newest,
//    and capped at MAX_INTENTS_PER_PAIR (oldest dropped).
//  - Cleared on a successful Remove of the collection, on sign-out / account
//    deletion / a switch to another user (the identity store reporting a
//    different user), and for one line when that line is definitively gone.
//  - A Retry that settles emits an IntentSettled event, so the surface that
//    started the change (the bulk bar) receives the operation's real
//    response when it is still mounted.
import { useSyncExternalStore } from 'react'
import type { RetryResult, WriteFailure } from '../hooks/useCopyMutations'
import * as identityStore from '../hooks/useSupabaseToken'
import { collectionOwnerOf } from './collectionOwner'

export const MAX_INTENTS_PER_PAIR = 50

export interface RetryIntent {
  id: number
  userId: string
  collectionId: string
  /** The copy line the change was for (absent for adds and bulk). */
  copyId?: number
  /** Every line the change touched (a bulk's ids; a line write's own id). */
  copyIds?: number[]
  /** The operation kind (patch, split, delete, add, bulk, undo). */
  kind: string
  /**
   * What the surface that started the change needs to report its confirmed
   * result later, whoever retries it (a take's binder name). Carried over to
   * a replacement intent: it re-runs the same spec.
   */
  meta?: unknown
  failure: WriteFailure
  retry: () => Promise<RetryResult>
}

let nextId = 0
let intents = new Map<string, readonly RetryIntent[]>()
const listeners = new Set<() => void>()
const NONE: readonly RetryIntent[] = []

function pairKey(userId: string, collectionId: string): string {
  return `${userId}#${collectionId}`
}

function publish(next: Map<string, readonly RetryIntent[]>): void {
  intents = next
  for (const l of listeners) l()
}

// --- identity: intents never outlive the user they were made for ------------

let watching = false

function watchIdentity(): void {
  if (watching) return
  watching = true
  try {
    const subscribe = identityStore.subscribeSupabaseIdentity
    if (typeof subscribe !== 'function') return
    subscribe(() => {
      // The collection OWNER (collectionOwnerOf): an anonymous session is a
      // guest, and a guest owns no per-copy intents.
      let owner: string | null | undefined
      try {
        owner = collectionOwnerOf(identityStore.getSupabaseIdentity())
      } catch {
        return
      }
      if (owner === undefined) return
      // Signed out, anonymous or another user: drop every other user's intents.
      if ([...intents.values()].some(list => list.some(i => i.userId !== owner))) {
        const next = new Map<string, readonly RetryIntent[]>()
        for (const [k, list] of intents) {
          const keep = list.filter(i => i.userId === owner)
          if (keep.length) next.set(k, keep)
        }
        publish(next)
      }
    })
  } catch {
    // No identity store (a test double without it): explicit clears still apply.
  }
}

// --- the registry -------------------------------------------------------------

export function addRetryIntent(input: Omit<RetryIntent, 'id'>): RetryIntent {
  watchIdentity()
  const intent: RetryIntent = { ...input, id: ++nextId }
  const key = pairKey(input.userId, input.collectionId)
  let list = (intents.get(key) ?? NONE).filter(i => !(
    input.copyId !== undefined && i.copyId === input.copyId && i.kind === input.kind
  ))
  list = [...list, intent]
  if (list.length > MAX_INTENTS_PER_PAIR) {
    // Only intents that lock no copy line are evicted, oldest first. One that
    // locks a line is kept until its Retry settles or it is dismissed, so the
    // cap can never silently unlock a line (Codex r3); past the cap the list
    // simply grows.
    let excess = list.length - MAX_INTENTS_PER_PAIR
    list = list.filter(i => {
      if (excess > 0 && i.copyId === undefined && !(i.copyIds?.length)) { excess--; return false }
      return true
    })
  }
  const next = new Map(intents)
  next.set(key, list)
  publish(next)
  return intent
}

/** The kept intents of a pair (does NOT remove them). */
export function peekRetryIntents(userId: string | null, collectionId: string | null): readonly RetryIntent[] {
  if (!userId || !collectionId) return NONE
  return intents.get(pairKey(userId, collectionId)) ?? NONE
}

/** Forget one intent: dismissed, its Retry settled, or replaced by a newer one. */
export function ackRetryIntent(intent: Pick<RetryIntent, 'id' | 'userId' | 'collectionId'>): void {
  const key = pairKey(intent.userId, intent.collectionId)
  const list = intents.get(key)
  if (!list || !list.some(i => i.id === intent.id)) return
  const next = new Map(intents)
  const keep = list.filter(i => i.id !== intent.id)
  if (keep.length) next.set(key, keep)
  else next.delete(key)
  publish(next)
}

/**
 * Clear kept intents: every one of a user (sign-out, account deletion), of
 * one collection (a successful Remove), or of one copy line in it (the line
 * is definitively gone).
 */
export function clearRetryIntents(filter: { userId: string; collectionId?: string; copyId?: number }): void {
  let changed = false
  const next = new Map<string, readonly RetryIntent[]>()
  for (const [k, list] of intents) {
    const keep = list.filter(i => !(
      i.userId === filter.userId
      && (filter.collectionId === undefined || i.collectionId === filter.collectionId)
      && (filter.copyId === undefined || i.copyId === filter.copyId)
    ))
    if (keep.length !== list.length) changed = true
    if (keep.length) next.set(k, keep)
  }
  if (changed) publish(next)
}

/**
 * True while any kept intent of the pair touches one of `copyIds`: an
 * unconfirmed change to those lines is still unresolved (not settled, not
 * dismissed).
 */
export function hasUnresolvedIntent(userId: string | null, collectionId: string | null, copyIds: readonly number[]): boolean {
  if (!userId || !collectionId || !copyIds.length) return false
  const ids = new Set(copyIds)
  return peekRetryIntents(userId, collectionId).some(i => (i.copyId !== undefined && ids.has(i.copyId)) || (i.copyIds ?? []).some(id => ids.has(id)))
}

/** The same for one line, live (the editor disables that line's controls). */
export function useLineHasUnresolvedIntent(userId: string | null, collectionId: string | null, copyId: number): boolean {
  return useSyncExternalStore(
    cb => { listeners.add(cb); return () => { listeners.delete(cb) } },
    () => hasUnresolvedIntent(userId, collectionId, [copyId]),
    () => false,
  )
}

/** The kept intents of a pair, live. */
export function useRetryIntents(userId: string | null, collectionId: string | null): readonly RetryIntent[] {
  return useSyncExternalStore(
    cb => { listeners.add(cb); return () => { listeners.delete(cb) } },
    () => peekRetryIntents(userId, collectionId),
    () => NONE,
  )
}

// --- settled events -------------------------------------------------------------

export interface IntentSettled {
  intentId: number
  /** The Retry's result, with the operation's own response in `value`. */
  result: RetryResult
}

const settledListeners = new Set<(e: IntentSettled) => void>()

export function emitIntentSettled(event: IntentSettled): void {
  for (const l of settledListeners) l(event)
}

export function subscribeIntentSettled(cb: (e: IntentSettled) => void): () => void {
  settledListeners.add(cb)
  return () => { settledListeners.delete(cb) }
}

/** Test seam. */
export function __resetRetryIntents(): void {
  publish(new Map())
}
